import React from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { BodyText, Form, TextLink, theme } from '@sweetgreen/garnish';

import { useIsLoggedIn } from '@order/AuthMachine';
import { PageWrapper } from '@order/components';
import { useCustomer } from '@order/Customer';
import { useLocalizationContext } from '@order/Localization';

import { type ForgotPasswordInput } from '../../graphql/types';
import type { ForgotPasswordScreenProps } from '../../navigation/AppNavigation.props';
import { useForgotPasswordForm } from './ForgotPasswordScreen.hooks';

// ────────────────────────────────────────────────────────────────────────────────

export const ForgotPasswordScreen = (props: ForgotPasswordScreenProps) => {
  const { navigation, route } = props;

  const { redirect: redirectParam } = route.params ?? {};
  const { forgotPassword: handleSubmitEmail, isFetchingCustomer } =
    useCustomer();
  const isLoggedIn = useIsLoggedIn();

  // ─────────────────────────────────────────────────────────────────

  const dismissModal = React.useCallback(() => {
    navigation.getParent()?.goBack();
  }, [navigation]);

  const handlePressSignIn = React.useCallback(() => {
    navigation.navigate('Login', { redirect: redirectParam });
  }, [navigation, redirectParam]);

  // ─────────────────────────────────────────────────────────────────

  React.useLayoutEffect(() => {
    if (isFetchingCustomer) return;

    if (isLoggedIn) {
      dismissModal();
    }
  }, [isFetchingCustomer, isLoggedIn, dismissModal]);

  // ────────────────────────────────────────────────────────────────────

  return (
    <ForgotPasswordForm
      testID="forgot-password-form"
      submitEmail={handleSubmitEmail}
      onPressSignIn={handlePressSignIn}
    />
  );
};

// ────────────────────────────────────────────────────────────────────────────────

export const ForgotPasswordForm = (props: ForgotPasswordFormProps) => {
  const { testID, submitEmail, onPressSignIn } = props;

  const { t } = useLocalizationContext();
  const { isForgotPasswordLoading } = useCustomer();

  const { form } = useForgotPasswordForm();
  const { control, formState, handleSubmit } = form;
  const { errors } = formState;

  // ────────────────────────────────────────────────────────────────────

  const onSubmit = React.useCallback(
    async (input: ForgotPasswordInput) => {
      await submitEmail(input);
    },
    [submitEmail],
  );

  const submitForm = React.useMemo(
    () => handleSubmit(onSubmit),
    [handleSubmit, onSubmit],
  );

  // ────────────────────────────────────────────────────────────────────

  return (
    <PageWrapper>
      <Form.Container testID={testID}>
        <Form.Title>{t('forgot-password.title')}</Form.Title>

        <Form.Subtitle>
          <BodyText size={3}>{t('forgot-password.subtitle')}</BodyText>
        </Form.Subtitle>

        <Form.TextField
          name="email"
          keyboardType="email-address"
          control={control}
          rules={{ required: true }}
          testID="field-email"
          label={t('form.email')}
          hideSubmitButton
          onSubmit={submitForm}
          autoComplete="email"
          notice={errors.email?.message}
        />

        <Form.SubmitButton
          testID="button-submit"
          isLoading={isForgotPasswordLoading}
          onPress={submitForm}
        >
          {t('general.submit')}
        </Form.SubmitButton>

        <View style={styles.linksWrapper}>
          <View style={styles.linksRow}>
            <BodyText size={3}>{t('forgot-password.link-text')} </BodyText>

            <TextLink
              testID="link-sign-in"
              accessibilityLabel={t('login.title')}
              onPress={onPressSignIn}
            >
              {t('login.title')}
            </TextLink>
          </View>
        </View>
      </Form.Container>
    </PageWrapper>
  );
};

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  linksWrapper: {
    paddingTop: theme.spacing['10'],
  },
  linksRow: {
    flexDirection: 'row',
    paddingBottom: theme.spacing['2'],
  },
});

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type ForgotPasswordInputProps = Parameters<
  ReturnType<typeof useCustomer>['forgotPassword']
>[0];

type ForgotPasswordFormProps = Readonly<{
  submitEmail: (input: ForgotPasswordInputProps) => Promise<void>;
  onPressSignIn: () => void;
}> &
  Pick<ViewProps, 'testID'>;
